/**
 * A module — publishing decisions.
 *
 * The twin of `flashbookPublish.ts`, and deliberately laid out the same way: a
 * published module is the same *two records* as a published capsule or book — a
 * post, which is what a feed carries, and the module record it points at, which
 * is what the playlist *is*. Keeping the decisions here, apart from the writes,
 * means the two things that must not drift can be tested on their own: which
 * repo write publishing uses, and which marks a module written for one classroom
 * carries.
 */
import {AtUri} from '@atproto/api'

import {CLASSROOM_ONLY_POST} from '#/state/classrooms/types'

/**
 * Which repo write publishing should use for the module record.
 *
 * A module built over more than one sitting already has a record — it is a draft
 * the whole time it is being assembled, which is what lets a creator leave and
 * come back, and what lets a capsule written *inside* the module find its way in.
 * Publishing is meant to *convert* that record: same rkey, `status: 'draft'` →
 * `'published'`, so the draft does not survive publishing as an orphan with no
 * post pointing at it. A module with no record behind it — a caller that built a
 * playlist in one go — creates one.
 */
export type ModulePublishTarget =
  | {mode: 'update'; rkey: string}
  | {mode: 'create'}

export function modulePublishTarget(
  draftUri?: string | null,
): ModulePublishTarget {
  if (!draftUri) return {mode: 'create'}
  return {mode: 'update', rkey: new AtUri(draftUri).rkey}
}

/**
 * Who a module is published *for*.
 *
 * `public` is the ordinary publish: the post goes on the creator's profile, the
 * module hangs off it, and any learner who finds it can play it. `classroom`
 * writes the same records with one difference on each: the module says
 * `visibility: 'classroom'` and so does the post, which is how the app knows to
 * keep a room's own course out of profiles, feeds and grids while the room that
 * holds it plays it. It is the answer the capsule publisher gives in the same
 * words, because it is the same rule about the same two records.
 */
export type ModuleAudience = 'public' | 'classroom'

/**
 * The marks a publish writes for its audience.
 *
 * Returned as data rather than applied inside the record builders, so the two
 * writes cannot disagree: the publisher spreads these onto the post record it
 * creates, and onto the same record again when it patches the module's
 * back-reference in — one answer, read twice. The post's mark is the same string
 * every other kind of work writes, which is what lets the app's hiding rule read
 * a post's record without caring what the post carries.
 */
export function moduleAudience(forClassroom: boolean): {
  postVisibility?: string
  recordVisibility?: string
} {
  return forClassroom
    ? {postVisibility: CLASSROOM_ONLY_POST, recordVisibility: 'classroom'}
    : {}
}

/**
 * The post a published module is carried by.
 *
 * `type: 'module'` is what makes the post recognisable as a playlist at all — the
 * same marker a capsule and a book write, and what a feed card reads before it
 * has asked for anything.
 *
 * `visibility` rides on *this* record rather than being added to the patch alone,
 * because publishing writes this record twice — created, then replaced with the
 * module's back-reference spread over it. A mark that only reached the second
 * write would be missing from a module whose patch failed, which for this field
 * means a room's own course sitting in the public feed.
 */
export function modulePostRecord({
  text,
  createdAt,
  visibility,
}: {
  text: string
  createdAt: string
  /** `CLASSROOM_ONLY_POST` when the module was written for one classroom. */
  visibility?: string
}) {
  return {
    $type: 'app.bsky.feed.post',
    text,
    type: 'module',
    createdAt,
    ...(visibility ? {visibility} : null),
  }
}

/**
 * What the post says about the module it holds.
 *
 * The second write: the same post again, carrying the back-ref that names the
 * module and the few facts a *card* can state before the module record has
 * arrived. A card is drawn in a scrolling feed, and a row that is blank until a
 * second request lands reads as a broken post — so the numbers a reader wants
 * anyway ride on the post that names it: how many capsules the playlist holds,
 * how much work is in it, and the two codes that say what the course is.
 *
 * The *capsules themselves* do not ride along, and that is the one place a module
 * differs from the work it gathers: a capsule's card is one record, and a
 * module's is a list of them. The card fetches them — from the module record's
 * own `items` — and draws the carousel that is the whole point of the card.
 */
export function modulePostFacts({
  moduleUri,
  moduleCid,
  itemCount,
  coins,
  level,
  subject,
}: {
  moduleUri: string
  moduleCid: string
  /** How many capsules the playlist holds. */
  itemCount: number
  /** Every exercise in every capsule, which is what the module pays out. */
  coins: number
  level?: string | null
  subject?: string | null
}) {
  return {
    module: {uri: moduleUri, cid: moduleCid},
    moduleSize: itemCount,
    moduleCoins: coins,
    ...(level ? {level} : null),
    ...(subject ? {subject} : null),
  }
}
